import { message, notification } from 'antd';
import { AxiosError } from 'axios';

import { cycloneApiClient } from '@/shared/api/core/axios-instance';

export type DownloadEntityType = 'dataset' | 'feature_set';

let isDownloading = false;

/** 자산 표시명이 비었을 때 archive·로컬 저장에 사용하는 기본 파일명 stem (확장자 제외) */
const DEFAULT_DOWNLOAD_FILENAME = 'download';

/**
 * 다운로드 zip stem (확장자 제외) — 자산(엔티티) 표시명만 사용합니다.
 * 파일 시스템·쿼리스트링에 안전하도록 비허용 문자는 `_` 로 치환하고, 연속 `_` 는 하나로 합니다.
 * 양끝의 `_`·`-` 는 제거합니다.
 */
const fileNameFromEntityName = (entityName?: string | null): string => {
  const raw = entityName?.trim();
  if (!raw) {
    return DEFAULT_DOWNLOAD_FILENAME;
  }

  const sanitized = raw
    .replace(/[^a-zA-Z0-9가-힣_-]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^[_-]+|[_-]+$/g, '');

  return sanitized.length > 0 ? sanitized : DEFAULT_DOWNLOAD_FILENAME;
};

/** 데모 다운로드용 Blob 크기 범위 (매 요청마다 이 사이에서 무작위) */
const DEMO_DOWNLOAD_MIN_BYTES = 100 * 1024;
const DEMO_DOWNLOAD_MAX_BYTES = 200 * 1024;

/**
 * parquet 대용 목업 바이너리 (실제 parquet 아님 — C 원시 버퍼에 가까운 무작위 바이트 채움)
 */
function createDemoMockParquetBlob(): Blob {
  const byteLength =
    DEMO_DOWNLOAD_MIN_BYTES + Math.floor(Math.random() * (DEMO_DOWNLOAD_MAX_BYTES - DEMO_DOWNLOAD_MIN_BYTES + 1));

  const buffer = new Uint8Array(byteLength);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    let offset = 0;
    const chunkSize = 65536;
    while (offset < byteLength) {
      const length = Math.min(chunkSize, byteLength - offset);
      crypto.getRandomValues(buffer.subarray(offset, offset + length));
      offset += length;
    }
  } else {
    for (let i = 0; i < byteLength; i++) {
      buffer[i] = Math.floor(Math.random() * 256);
    }
  }

  return new Blob([buffer], { type: 'application/octet-stream' });
}

function triggerBlobDownload(blob: Blob, downloadFileName: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = downloadFileName;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * 자산 데이터 다운로드 (Zip Archive)
 *
 * @description
 * Dataset 또는 Feature Set의 데이터 파일들을 zip으로 묶어 다운로드합니다.
 * API 서버가 S3에서 Parquet 파일을 청크 단위로 읽어 zip 스트림으로 변환하므로,
 * 클라이언트는 단일 HTTPS 요청으로 모든 파일을 다운로드할 수 있습니다.
 *
 * **동시성 제한**:
 * - 전체 서버에서 동시에 1개의 archive 다운로드만 허용됩니다.
 * - 이미 진행 중인 다운로드가 있으면 429를 반환합니다.
 *
 * @param entityType - 자산 타입 (dataset 또는 feature_set)
 * @param entityId - 자산 ID
 * @param snapshotId - 스냅샷 ID (선택, 대용량 정수를 문자열로 전달)
 * @param entityDisplayName - 표시명(데이터셋/피처셋 **`table`** 값과 동일). API `title`·`namespace` 접두 없이 사용자가 이름으로 보는 식별자입니다.
 */
export const downloadAssetData = async (
  entityType: DownloadEntityType,
  entityId: number,
  snapshotId?: string | null,
  entityDisplayName?: string | null
): Promise<void> => {
  if (isDownloading) {
    notification.warning({
      message: '다운로드 진행 중',
      description: '이미 다운로드가 진행 중입니다. 완료 후 다시 시도해주세요.',
      duration: 3,
    });
    return;
  }

  try {
    isDownloading = true;
    message.loading({ content: '다운로드를 준비하는 중...', key: 'download', duration: 0 });

    const customFileName = fileNameFromEntityName(entityDisplayName);

    // 데모: archive API 없이 엔티티 표시명 stem + .parquet, 내용은 100~200KB 무작위 바이너리(목업)
    if (import.meta.env.MODE === 'demo') {
      triggerBlobDownload(createDemoMockParquetBlob(), `${customFileName}.parquet`);
      message.success({ content: '다운로드가 완료되었습니다.', key: 'download', duration: 3 });
      return;
    }

    const params: Record<string, string | number> = {
      entityType,
      entityId,
    };

    if (snapshotId != null && snapshotId !== '') {
      params.snapshotId = snapshotId;
    }

    if (customFileName) {
      params.filename = customFileName;
    }

    const response = await cycloneApiClient.get('/downloads/archive', {
      params,
      responseType: 'blob',
      skipGlobalErrorNotification: true,
    });

    const blob = new Blob([response.data], { type: 'application/zip' });
    triggerBlobDownload(blob, `${customFileName}.zip`);

    message.success({ content: '다운로드가 완료되었습니다.', key: 'download', duration: 3 });
  } catch (error) {
    console.error('Download failed:', error);

    if (error instanceof AxiosError && error.response?.status === 429) {
      notification.warning({
        message: '동시 다운로드 제한',
        description: '동시 다운로드가 많아 잠시 후 다시 시도해주세요.',
        duration: 5,
      });
      message.destroy('download');
    } else if (error instanceof AxiosError && error.response?.status === 404) {
      message.error({ content: '다운로드할 데이터를 찾을 수 없습니다.', key: 'download', duration: 3 });
    } else {
      message.error({ content: '다운로드에 실패했습니다.', key: 'download', duration: 3 });
    }
    throw error;
  } finally {
    isDownloading = false;
  }
};
